/**
 * A Markdown renderer for the application's own documents, the Privacy Policy
 * first (issue #56). It builds React elements, never HTML strings, so nothing
 * in a document can become markup the page did not intend.
 *
 * DELIBERATELY SMALL. It handles what `policies/*.md` use and nothing more:
 * `#` to `###` headings, paragraphs, `-` lists (one level, a wrapped item
 * continuing on indented lines), `|` tables, `---` rules, and inline
 * `**bold**`, `` `code` `` and `[text](https://…)`. A document that needs more
 * should grow this, with a test, rather than pull in a parser for a page of
 * prose. A link is rendered only for an absolute https URL, so a stray
 * `javascript:` link in a document is printed as text instead.
 */

import type { ReactNode } from 'react';

const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\((https:\/\/[^)\s]+)\)/g;

export function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    if (match[1] !== undefined) out.push(<strong key={key++}>{renderInline(match[1])}</strong>);
    else if (match[2] !== undefined) out.push(<code key={key++}>{match[2]}</code>);
    else out.push(<a key={key++} href={match[4]} rel="noopener noreferrer">{match[3]}</a>);
    last = at + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function cells(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

export function renderMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (line.trim() === '') {
      i += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      const content = renderInline(heading[2]!);
      blocks.push(level === 1 ? <h1 key={key++}>{content}</h1> : level === 2 ? <h2 key={key++}>{content}</h2> : <h3 key={key++}>{content}</h3>);
      i += 1;
      continue;
    }

    if (/^-{3,}\s*$/.test(line)) {
      blocks.push(<hr key={key++} />);
      i += 1;
      continue;
    }

    if (line.startsWith('|')) {
      const rows: string[] = [];
      while (i < lines.length && lines[i]!.startsWith('|')) {
        rows.push(lines[i]!);
        i += 1;
      }
      const [head, , ...body] = rows;
      blocks.push(
        <table key={key++}>
          <thead>
            <tr>{cells(head!).map((cell, c) => <th key={c}>{renderInline(cell)}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>{cells(row).map((cell, c) => <td key={c}>{renderInline(cell)}</td>)}</tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }

    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i]!.startsWith('- ') || /^\s+\S/.test(lines[i]!))) {
        const current = lines[i]!;
        if (current.startsWith('- ')) items.push(current.slice(2).trim());
        else items[items.length - 1] += ` ${current.trim()}`;
        i += 1;
      }
      blocks.push(
        <ul key={key++}>
          {items.map((item, n) => <li key={n}>{renderInline(item)}</li>)}
        </ul>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !/^(#{1,3}\s|- |\||-{3,}\s*$)/.test(lines[i]!)
    ) {
      paragraph.push(lines[i]!.trim());
      i += 1;
    }
    blocks.push(<p key={key++}>{renderInline(paragraph.join(' '))}</p>);
  }

  return blocks;
}
